import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, X } from 'react-feather'
import PropTypes from 'prop-types'

import { MAX_DOCUMENT_SIZE } from '../../helpers/document-upload'

const FileUpload = ({
  onFileSelect,
  onFileRemove,
  acceptedFileTypes = '.pdf,.doc,.docx',
  maxFileSize = MAX_DOCUMENT_SIZE,
  multiple = false,
  files = [],
  description = undefined
}) => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState(null)

  const validateFile = useCallback(
    (file) => {
      const fileType = file.name.split('.').pop().toLowerCase()
      const acceptedTypes = acceptedFileTypes
        .split(',')
        .map((type) => type.replace('.', ''))

      if (!acceptedTypes.includes(fileType)) {
        throw new Error(t('file_upload.error.invalid_type'))
      }

      if (file.size > maxFileSize) {
        throw new Error(t('file_upload.error.file_too_large'))
      }

      return true
    },
    [acceptedFileTypes, maxFileSize, t]
  )

  const handleFiles = useCallback(
    (newFiles) => {
      setError(null)

      try {
        const validFiles = newFiles.filter((file) => {
          try {
            return validateFile(file)
          } catch (err) {
            setError(err.message)
            return false
          }
        })

        if (validFiles.length === 0) return

        onFileSelect(validFiles)
      } catch (err) {
        setError(t('file_upload.error.general'))
      }
    },
    [onFileSelect, t, validateFile]
  )

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const droppedFiles = Array.from(e.dataTransfer.files)
      handleFiles(droppedFiles)
    },
    [handleFiles]
  )

  const handleFileInput = useCallback(
    async (e) => {
      const selectedFiles = Array.from(e.target.files)
      handleFiles(selectedFiles)
    },
    [handleFiles]
  )

  return (
    <div className='w-full'>
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type='file'
          className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
          onChange={handleFileInput}
          accept={acceptedFileTypes}
          multiple={multiple}
        />
        <div className='space-y-2'>
          <Upload className='mx-auto h-12 w-12 text-gray-400' />
          <div className='text-sm text-gray-600'>
            <p>{t('file_upload.drag_drop')}</p>
            <p className='text-xs mt-1'>{t('file_upload.or_click')}</p>
          </div>
          <p className='text-xs text-gray-500'>
            {t('file_upload.accepted_types', { types: acceptedFileTypes })}
          </p>
        </div>
      </div>

      {error && <div className='mt-2 text-sm text-red-600'>{error}</div>}

      {files.length > 0 && (
        <div className='mt-4 space-y-2'>
          <p className='text-sm text-gray-500'>
            {t('file_upload.selected_files', { count: files.length })}
          </p>
          {files.map((file, index) => (
            <div
              key={index}
              className='flex items-center justify-between p-2 bg-gray-50 rounded'
            >
              <span className='text-sm truncate'>{file.name}</span>
              <button
                type='button'
                onClick={() => onFileRemove(index)}
                className='text-gray-400 hover:text-gray-600'
                title={t('file_upload.remove_file')}
              >
                <X className='h-4 w-4' />
              </button>
            </div>
          ))}
        </div>
      )}

      {description && (
        <p className='text-gray-500 text-xs mt-2 italic'>{description}</p>
      )}
    </div>
  )
}

FileUpload.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.string,
  maxFileSize: PropTypes.number,
  multiple: PropTypes.bool,
  files: PropTypes.array,
  description: PropTypes.string
}

export default FileUpload
